import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import characterService from '../../services/characterService';

const CharactersLibrary = () => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [filteredCharacters, setFilteredCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [playFilter, setPlayFilter] = useState('all');

  useEffect(() => {
    fetchCharacters();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [characters, searchQuery, sortBy, playFilter]);

  const fetchCharacters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await characterService?.getAllCharacters();
      setCharacters(data || []);
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError(err?.message || 'Failed to load characters');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...characters];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(char =>
        char?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        char?.playTitle?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Play filter
    if (playFilter !== 'all') {
      filtered = filtered?.filter(char => char?.playTitle === playFilter);
    }

    // Sorting
    if (sortBy === 'popularity') {
      filtered?.sort((a, b) => (b?.popularityScore || 0) - (a?.popularityScore || 0));
    } else if (sortBy === 'alphabetical') {
      filtered?.sort((a, b) => a?.name?.localeCompare(b?.name));
    } else if (sortBy === 'lines') {
      filtered?.sort((a, b) => (b?.lineCount || 0) - (a?.lineCount || 0));
    }

    setFilteredCharacters(filtered);
  };

  const handleCharacterClick = (character) => {
    navigate('/character-detail-page', {
      state: {
        playId: character?.playId,
        characterName: character?.name
      }
    });
  };

  const getUniquePlayTitles = () => {
    const titles = new Set(characters?.map(c => c?.playTitle)?.filter(Boolean));
    return Array.from(titles)?.sort();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation
          user={userProfile}
          isAuthenticated={isAuthenticated}
          notifications={[]}
        />
        <main className="main-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <p className="text-muted-foreground">Loading characters...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation
          user={userProfile}
          isAuthenticated={isAuthenticated}
          notifications={[]}
        />
        <main className="main-content">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Characters</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchCharacters}>Try Again</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">Characters Library</h1>
            <p className="text-lg text-muted-foreground">
              Explore characters from Shakespeare's plays with detailed information and voice performances
            </p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="Users2" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Characters</p>
                  <p className="text-2xl font-bold text-card-foreground">{characters?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                  <Icon name="BookOpen" size={24} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Plays Featured</p>
                  <p className="text-2xl font-bold text-card-foreground">{getUniquePlayTitles()?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                  <Icon name="Mic2" size={24} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Voice Actors</p>
                  <p className="text-2xl font-bold text-card-foreground">
                    {new Set(characters?.map(c => c?.voiceActorId)?.filter(Boolean))?.size || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Search Characters
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    type="text"
                    placeholder="Search by name or play..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Filter by Play
                </label>
                <Select
                  value={playFilter}
                  onChange={(e) => setPlayFilter(e?.target?.value)}
                >
                  <option value="all">All Plays</option>
                  {getUniquePlayTitles()?.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Sort By
                </label>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                >
                  <option value="popularity">Popularity</option>
                  <option value="alphabetical">Alphabetical (A-Z)</option>
                  <option value="lines">Line Count</option>
                </Select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredCharacters?.length} of {characters?.length} characters
            </p>
          </div>

          {/* Character Cards Grid */}
          {filteredCharacters?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon name="Users2" size={64} className="text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Characters Found</h3>
              <p className="text-muted-foreground text-center max-w-md">
                {searchQuery || playFilter !== 'all' ?'Try adjusting your search or filters' :'No characters available in the database'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCharacters?.map((character) => (
                <div
                  key={`${character?.playId}-${character?.name}`}
                  onClick={() => handleCharacterClick(character)}
                  className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
                >
                  {/* Character Image */}
                  <div className="relative h-48 bg-muted overflow-hidden">
                    {character?.imageUrl ? (
                      <img
                        src={character?.imageUrl}
                        alt={`${character?.name} from ${character?.playTitle}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-200"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                        <Icon name="User" size={64} className="text-primary/30" />
                      </div>
                    )}
                    {/* Role Badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-background/90 backdrop-blur-sm text-foreground capitalize">
                        {character?.roleClassification || 'Supporting'}
                      </span>
                    </div>
                  </div>

                  {/* Character Info */}
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-card-foreground mb-1 group-hover:text-primary transition-colors">
                      {character?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">{character?.playTitle}</p>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-card-foreground">
                          {character?.lineCount} lines
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Film" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-card-foreground">
                          {character?.sceneAppearances || 0} scenes
                        </span>
                      </div>
                    </div>

                    {/* Voice Actor */}
                    {character?.voiceActorName && (
                      <div className="flex items-center gap-2 pt-3 border-t border-border">
                        <Icon name="Mic2" size={14} className="text-primary" />
                        <span className="text-xs text-muted-foreground">
                          Voiced by {character?.voiceActorName}
                        </span>
                      </div>
                    )}

                    {/* AI Content Status Badges */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          character?.aiAnalysis
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-muted text-muted-foreground'
                        }`}
                        title={character?.aiAnalysis ? 'Description generated' : 'Description not yet generated'}
                      >
                        <Icon
                          name={character?.aiAnalysis ? 'CheckCircle' : 'Circle'}
                          size={11}
                          className={character?.aiAnalysis ? 'text-emerald-600' : 'text-muted-foreground'}
                        />
                        Desc
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          character?.aiQuotes && character?.aiQuotes?.length > 0
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-muted text-muted-foreground'
                        }`}
                        title={character?.aiQuotes && character?.aiQuotes?.length > 0 ? 'Quotes generated' : 'Quotes not yet generated'}
                      >
                        <Icon
                          name={character?.aiQuotes && character?.aiQuotes?.length > 0 ? 'CheckCircle' : 'Circle'}
                          size={11}
                          className={character?.aiQuotes && character?.aiQuotes?.length > 0 ? 'text-emerald-600' : 'text-muted-foreground'}
                        />
                        Quotes
                      </span>
                    </div>

                    {/* Popularity Score */}
                    {character?.popularityScore > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Icon name="TrendingUp" size={14} className="text-warning" />
                        <span className="text-xs text-muted-foreground">
                          Popularity: {character?.popularityScore}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CharactersLibrary;